import {Component, ElementRef, inject, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {AppMenuitem} from '@/layout/components/app.menuitem';
import {UserRolesService} from "@/features/services/user-roles.service";

interface MenuItem {
    label?: string;
    icon?: string;
    routerLink?: string[];
    url?: string[];
    target?: '_blank' | '_self' | '_parent' | '_top';
    routerLinkActiveOptions?: { [key: string]: any };
    items?: MenuItem[];
    separator?: boolean;
    visible?: boolean;
    disabled?: boolean;
    command?: (event?: any) => void;
    class?: string;
    style?: string;
    styleClass?: string;
    id?: string;
    urlTarget?: '_blank' | '_self' | '_parent' | '_top';
}

const ICON = {
    book: 'fa-solid fa-book',
    home: 'fa-solid fa-house',
    dashboard: 'fa-solid fa-chart-column',
    calendar: 'fa-solid fa-calendar',
    contracts: 'fa-solid fa-scroll',
    search: 'fa-solid fa-magnifying-glass',
    searchContract: 'fa-solid fa-file-contract',
    searchReceipt: 'fa-solid fa-receipt',
    processing: 'fa-solid fa-gears',
    launcher: 'fa-solid fa-play',
    paymentCheck: 'fa-solid fa-money-check-dollar',
    registry: 'fa-solid fa-id-card',
    products: 'fa-solid fa-suitcase',
    campaigns: 'fa-solid fa-share-nodes',
    campaignsLink: 'fa-solid fa-link',
    campaignsToLink: 'fa-solid fa-link-slash',
    registryPharma: 'fa-solid fa-prescription-bottle-medical',
    salesNetwork: 'fa-solid fa-network-wired',
    userGear: 'fa-solid fa-user-gear',
    socialStatus: 'fa-brands fa-instagram'
} as const;

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: ` <ul class="layout-menu" #menuContainer>
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul>`,
    host: {
        class: 'layout-menu-container'
    }
})
export class AppMenu {
    private userRoleSvc = inject(UserRolesService);

    @ViewChild('menuContainer') menuContainer!: ElementRef;

    model: MenuItem[];

    ngOnInit() {
        const isSales = this.userRoleSvc.isZentivaSales();
        const isSalesOrCorp = this.userRoleSvc.isZentivaSales() || this.userRoleSvc.isZentivaCorp();
        const isHvs = this.userRoleSvc.isHvsUser() || this.userRoleSvc.isHvsSupervisor();

        const raw: MenuItem[] = [
            {
                items: [
                    {
                        label: 'menu.dashboard.title',
                        icon: ICON.home,
                        routerLink: ['/dashboard']
                    }
                ]
            },
            {
                items: [
                    {
                        label: 'menu.contracts.title',
                        icon: ICON.contracts,
                        items: [
                            {
                                label: 'menu.contracts.search',
                                icon: ICON.searchContract,
                                routerLink: ['/contracts/search']
                            },
                            {
                                label: 'menu.contracts.search-installments',
                                icon: ICON.searchReceipt,
                                routerLink: ['/installments/search'],
                                visible: isHvs
                            },
                            {
                                label: 'menu.contracts.social-status',
                                icon: ICON.socialStatus,
                                routerLink: ['/contracts/social-status']
                            }
                        ]
                    }
                ]
            },
            {
                visible: isHvs,
                items: [
                    {
                        label: 'menu.schedule.title',
                        icon: ICON.book,
                        routerLink: ['/schedule/search']
                    }
                ]
            },
            {
                visible: !isSalesOrCorp,
                items: [
                    {
                        label: 'menu.processing.title',
                        icon: ICON.processing,
                        items: [
                            {
                                label: 'menu.processing.search',
                                icon: ICON.search,
                                routerLink: ['/processing/search']
                            },
                            {
                                label: 'menu.processing.launcher',
                                icon: ICON.launcher,
                                routerLink: ['/processing/launcher']
                            },
                            {
                                label: 'menu.processing.check-payments',
                                icon: ICON.paymentCheck,
                                routerLink: ['/processing/check-payments']
                            }
                        ]
                    }
                ]
            },
            {
                visible: !isSalesOrCorp,
                items: [
                    {
                        label: 'menu.registry.title',
                        icon: ICON.registry,
                        items: [
                            {
                                label: 'menu.registry.pharma',
                                icon: ICON.search,
                                routerLink: ['/registry', 'pharma']
                            },
                            {
                                label: 'menu.registry.sales',
                                icon: ICON.salesNetwork,
                                routerLink: ['/registry', 'sales']
                            }
                        ]
                    }
                ]
            },
            {
                visible: !isSalesOrCorp,
                items: [
                    {
                        label: 'menu.products.title',
                        icon: ICON.products,
                        items: [
                            {
                                label: 'menu.products.search',
                                icon: ICON.search,
                                routerLink: ['/products/search']
                            }
                        ]
                    }
                ]
            },
            {
                items: [
                    {
                        label: 'menu.campaigns.title',
                        icon: ICON.campaigns,
                        items: [
                            {
                                label: 'menu.campaigns.subscribe',
                                icon: ICON.campaignsLink,
                                routerLink: ['/campaigns/search/enrolled']
                            },
                            {
                                visible: !isSalesOrCorp,
                                label: 'menu.campaigns.notSubscribed',
                                icon: ICON.campaignsToLink,
                                routerLink: ['/campaigns/search/not-enrolled']
                            }
                        ]
                    }
                ]
            }
        ];

        this.model = this.filterInvisible(raw);
    }

    // Rimuove sezioni/nodi totalmente nascosti (o senza figli dopo il filtro)
    private filterInvisible(items: MenuItem[]): MenuItem[] {
        return items
            .filter(i => i.visible !== false)
            .map(i => {
                const kids = i.items ? this.filterInvisible(i.items) : undefined;
                return kids ? { ...i, items: kids } : i;
            })
            .filter(i => !i.items || i.items.length > 0);
    }
}
